import { ChangeEvent, Dispatch, useEffect, useReducer } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';

import { CustomCheckBox, CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { validateName } from '../../../shared/Helpers/functions';
import { useIsTelehealth, useParentTrainingOptions } from '../hook';
import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	initialParentTrainingErrorState,
	parentTrainingErrorReducer,
} from './parentTrainingReducer';
import { Behaviors, SignatureInput, Targets, TelehealthLocations, TimeInfo } from '../components';
import { useSoapNoteFieldDisabled } from '../helpers';

import { useSelector } from '../../../redux/store';
import { UpdateParentTrainingProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const ParentTrainingData = ({
	data,
	setData,
	save,
}: {
	data: UpdateParentTrainingProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const today = new Date();
	const todayString = dayjs(today).format('MM/DD/YYYY');

	const locationList = useSelector((state) => state.soapNote.locations);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const { directParentTrainingList, peopleInAttendanceList } = useParentTrainingOptions();
	const isTelehealth = useIsTelehealth(data.locationId);
	const { getFieldDisabled } = useSoapNoteFieldDisabled();

	const [errors, setErrors] = useReducer(parentTrainingErrorReducer, initialParentTrainingErrorState);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = peopleInAttendanceList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setAttendance, payload });
	}, [peopleInAttendanceList, setData, data]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	const attendanceHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setAttendance, payload: value });
		if (!errors.Attendance) return;
		setErrors({ payload: false, type: ERRORS.Attendance });
	};

	const directParentTraningHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setDirectParentTraining, payload: value });
		if (!errors.DirectParentTraining) return;
		setErrors({ payload: false, type: ERRORS.DirectParentTraining });
	};

	const parentFollowingPlanHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setParentFollowingPlan,
			payload: checked,
		});
		if (!errors.ParentFollowingPlan) return;
		setErrors({ payload: false, type: ERRORS.ParentFollowingPlan });
	};

	const antecedentBehaviorAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setAntecedentBehaviorAddressed, payload: value });
		if (!errors.AntecedentBehaviorAddressed) return;
		setErrors({ payload: false, type: ERRORS.AntecedentBehaviorAddressed });
	};

	const behaviorAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setBehaviorAddressed, payload: value });
		if (!errors.BehaviorAddressed) return;
		setErrors({ payload: false, type: ERRORS.BehaviorAddressed });
	};

	const consequenceBehaviorAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setConsequenceBehaviorAddressed, payload: value });
		if (!errors.ConsequenceBehaviorAddressed) return;
		setErrors({ payload: false, type: ERRORS.ConsequenceBehaviorAddressed });
	};

	const behavioralMethodsUsedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setBehavioralMethodsUsed, payload: value });
		if (!errors.BehavioralMethodsUsed) return;
		setErrors({ payload: false, type: ERRORS.BehavioralMethodsUsed });
	};

	const goalsAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setGoalsAddressed, payload: value });
		if (!errors.GoalsAddressed) return;
		setErrors({ payload: false, type: ERRORS.GoalsAddressed });
	};

	const barriersToTreatmentHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setBarriers, payload: value });
		if (!errors.Barriers) return;
		setErrors({ payload: false, type: ERRORS.Barriers });
	};

	const parentResponseToInterventionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setParentResponseToIntervention, payload: value });
		if (!errors.ParentResponseToIntervention) return;
		setErrors({ payload: false, type: ERRORS.ParentResponseToIntervention });
	};

	const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setSessionSummary, payload: value });
		if (!errors.SessionSummary) return;
		setErrors({ payload: false, type: ERRORS.SessionSummary });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setSignature, payload: value.toUpperCase() });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setParentTrainingParticipantOther });
		if (!errors.ParentTrainingParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.ParentTrainingParticipantOther });
	};

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => peopleInAttendanceList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<div>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			<CustomSelect
				label="Location:"
				data={locationList}
				value={data.locationId ? data.locationId : ''}
				setValue={locationHandler}
				className="marginBottom16"
				error={errors.LocationId}
				disabled={getFieldDisabled('locationId')}
				onBlur={save}
			/>
			{isTelehealth && (
				<TelehealthLocations
					setData={setData}
					data={data}
					disabled={getFieldDisabled('clientTelehealthLocationId')}
					save={save}
				/>
			)}
			<MultipleSelect
				label="Session Participants:"
				data={peopleInAttendanceList}
				initialValue={data.participantResponses}
				setValue={attendanceHandler}
				className="marginBottom16"
				error={errors.Attendance}
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.parentTrainingParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('participantOther')}
					onBlur={save}
					error={errors.ParentTrainingParticipantOther}
				/>
			)}
			<MultipleSelect
				label="Direct parent training:"
				data={directParentTrainingList}
				initialValue={data.directParentTrainingResponses}
				setValue={directParentTraningHandler}
				className="marginBottom16"
				error={errors.DirectParentTraining}
				disabled={getFieldDisabled('directParentTrainingResponses')}
				onBlur={save}
			/>
			<CustomCheckBox
				item={{
					label: `Was parent following
              behavior plan?:`,
					checked: data.parentFollowing,
					id: '0',
					disabled: getFieldDisabled('parentFollowing'),
				}}
				onChange={parentFollowingPlanHandler}
				onBlur={save}
			/>
			<CustomInput
				label="Antecedent to behavior addressed:"
				value={data.antecedent}
				onChange={antecedentBehaviorAddressedHandler}
				multiline
				className="marginBottom16"
				error={errors.AntecedentBehaviorAddressed}
				disabled={getFieldDisabled('antecedent')}
				onBlur={save}
			/>
			<CustomInput
				label="Behavior addressed:"
				value={data.behavior}
				onChange={behaviorAddressedHandler}
				multiline
				error={errors.BehaviorAddressed}
				className="marginBottom16"
				disabled={getFieldDisabled('behavior')}
				onBlur={save}
			/>
			<CustomInput
				label="Consequence to behavior addressed:"
				value={data.consequence}
				onChange={consequenceBehaviorAddressedHandler}
				multiline
				className="marginBottom16"
				error={errors.ConsequenceBehaviorAddressed}
				disabled={getFieldDisabled('consequence')}
				onBlur={save}
			/>
			<CustomInput
				label="Behavioral methods used:"
				value={data.behavioralMethodsUsed}
				onChange={behavioralMethodsUsedHandler}
				multiline
				className="marginBottom16"
				disabled={getFieldDisabled('behavioralMethodsUsed')}
				error={errors.BehavioralMethodsUsed}
				onBlur={save}
			/>
			<CustomInput
				label="Goal addressed:"
				value={data.goalsAddressed}
				onChange={goalsAddressedHandler}
				multiline
				className="marginBottom16"
				disabled={getFieldDisabled('goalsAddressed')}
				error={errors.GoalsAddressed}
				onBlur={save}
			/>
			<CustomInput
				label="Barriers to treatment:"
				value={data.barriersToTreatment}
				onChange={barriersToTreatmentHandler}
				multiline
				className="marginBottom16"
				error={errors.Barriers}
				disabled={getFieldDisabled('barriersToTreatment')}
				onBlur={save}
			/>
			<CustomInput
				label="Parent response to intervention:"
				value={data.parentResponseToIntervention}
				onChange={parentResponseToInterventionHandler}
				multiline
				error={errors.ParentResponseToIntervention}
				className="marginBottom16"
				disabled={getFieldDisabled('parentResponseToIntervention')}
				onBlur={save}
			/>
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<CustomInput
				label="Session Summary: "
				value={data.sessionSummary}
				error={errors.SessionSummary}
				onChange={sessionSummaryHandler}
				className="marginBottom8"
				multiline
				disabled={getFieldDisabled('sessionSummary')}
				onBlur={save}
			/>
			<SignatureInput
				value={data.signature}
				setValue={signatureHandler}
				disabled={getFieldDisabled('signature')}
				error={errors.Signature}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date of signature:" type="psm" fontWeight="semiBold" />
				<Text title={todayString} type="psm" />
			</div>
		</div>
	);
};

export default ParentTrainingData;
